import React, { useState } from 'react';
import { Box, Button, Card, Container, Typography, Select, MenuItem, FormControl, InputLabel, CircularProgress, Stack, Chip, IconButton, Tooltip, Skeleton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth0 } from "@auth0/auth0-react";
import CONFIG from 'utils/config';
import { useWorkspaces } from 'contexts/WorkspacesContext';

// Import custom hooks
import { useGetWorkspaceSubscriptions, WorkspaceSubscriptionResponse } from 'api/workspace/subscriptions/queries/useGetWorkspaceSubscriptions';
import { useCreateSubscription, SubscriptionResponse, CreateSubscriptionParams } from 'api/workspace/subscriptions/mutations/useCreateSubscription';
import { useDeleteSubscription, DeleteSubscriptionParams } from 'api/workspace/subscriptions/mutations/useDeleteSubscription';
import { usePauseSubscription, PauseSubscriptionParams } from 'api/workspace/subscriptions/mutations/usePauseSubscription';
import { useResumeSubscription, ResumeSubscriptionParams } from 'api/workspace/subscriptions/mutations/useResumeSubscription';

// Use Razorpay key from environment config
const RAZORPAY_KEY_ID = CONFIG.RAZORPAY_KEY_ID;

declare global {
  interface Window {
    Razorpay: any;
  }
}

// Interface definitions moved to the query/mutation files

// Define standardized plan names for API communication
const PLAN_NAMES = {
  ENTERPRISE: 'enterprise',
  PROFESSIONAL: 'professional',
  STARTER: 'starter',
  PAY_AS_YOU_GO: 'payAsYouGo'
};

// Filter out any plans with undefined IDs and provide type safety
const plans = [
  { name: CONFIG.RAZORPAY_PLANS.ENTERPRISE || '', amount: 400, interval: 'monthly', displayName: 'Enterprise', standardName: PLAN_NAMES.ENTERPRISE, contactOnly: true },
  { name: CONFIG.RAZORPAY_PLANS.PROFESSIONAL || '', amount: 300, interval: 'monthly', displayName: 'Professional', standardName: PLAN_NAMES.PROFESSIONAL },
  { name: CONFIG.RAZORPAY_PLANS.STARTER || '', amount: 200, interval: 'monthly', displayName: 'Starter', standardName: PLAN_NAMES.STARTER },
  { name: CONFIG.RAZORPAY_PLANS.PAY_AS_YOU_GO || '', amount: 100, interval: 'monthly', displayName: 'Pay as you go', standardName: PLAN_NAMES.PAY_AS_YOU_GO }
];

// Filter out any plans with empty IDs
const validPlans = plans.filter(plan => plan.name);

const ExperimentalPayments: React.FC = () => {
  const { activeWorkspace } = useWorkspaces();
  const { user } = useAuth0();
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [showContactDialog, setShowContactDialog] = useState<boolean>(false);

  const isStaff = user && user["https://app.preimage.ai/roles"]?.includes(CONFIG.ROLES.STAFF);
  const isOwner = activeWorkspace?.role === 'OWNER';
  const hasSubscriptionManagement = activeWorkspace?.featureFlags?.includes('enable-subscriptions');

  // Use the custom hook for getting the subscription
  const { data: subscription, isLoading: checkingSubscription, isFetching: isFetchingSubscription, refetch: refetchSubscription } = useGetWorkspaceSubscriptions(
    activeWorkspace?.slug || '',
    hasSubscriptionManagement || false
  );
  
  // Use a combined loading state to show loading UI during initial load and refetching
  const isLoadingSubscription = checkingSubscription || isFetchingSubscription;
  
  // Function to handle manual refresh
  const handleRefreshSubscription = () => {
    refetchSubscription();
  };

  // Use the custom hook for creating a subscription
  const createSubscriptionMutation = useCreateSubscription();
  const loading = createSubscriptionMutation.isPending;
  
  // Use the custom hook for deleting a subscription
  const deleteSubscriptionMutation = useDeleteSubscription();
  const deletingSubscription = deleteSubscriptionMutation.isPending;
  
  // Use the custom hook for pausing a subscription
  const pauseSubscriptionMutation = usePauseSubscription();
  const pausingSubscription = pauseSubscriptionMutation.isPending;
  
  // Use the custom hook for resuming a subscription
  const resumeSubscriptionMutation = useResumeSubscription();
  const resumingSubscription = resumeSubscriptionMutation.isPending;

  // Early return if feature flag is not enabled
  if (!hasSubscriptionManagement) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Subscriptions
        </Typography>
        <Typography variant="body1">
          Subscription management is not available for this workspace.
        </Typography>
      </Container>
    );
  }

  const handlePayment = (subscriptionData: SubscriptionResponse) => {
    console.log('Starting payment with data:', subscriptionData);
    console.log('Using Razorpay key:', RAZORPAY_KEY_ID);
    
    if (!RAZORPAY_KEY_ID) {
      console.error('Razorpay key is missing');
      alert('Configuration error: Razorpay key is missing');
      return;
    }

    const options = {
      key: RAZORPAY_KEY_ID,
      subscription_id: subscriptionData.id,
      name: 'Preimage',
      description: 'Subscription Payment',
      handler: function (response: any) {
        console.log('Payment success response:', response);
        // Refresh subscription status after successful payment
        window.location.reload();
      },
      prefill: {
        name: 'Test User',
        email: 'test@example.com',
        contact: '9999999999'
      },
      notes: {
        plan_id: selectedPlan
      },
      theme: {
        color: '#3F51B5'
      }
    };

    console.log('Razorpay options:', options);
    const razorpayInstance = new window.Razorpay(options);
    razorpayInstance.open();
  };
  
  const handleCreateSubscription = () => {
    if (!selectedPlan) {
      alert('Please select a plan');
      return;
    }
    
    // Find the selected plan object to get additional details if needed
    const selectedPlanObj = plans.find(plan => plan.name === selectedPlan);
    if (!selectedPlanObj) {
      alert('Invalid plan selected');
      return;
    }
    
    // If this is the Enterprise plan, show contact dialog instead of processing payment
    if (selectedPlanObj.contactOnly) {
      setShowContactDialog(true);
      return;
    }
    
    // Send the standardized plan name for API compatibility
    const params: CreateSubscriptionParams = { 
      workspaceId: activeWorkspace.id,
      planName: selectedPlanObj.standardName
    };
    
    createSubscriptionMutation.mutate(params, {
      onSuccess: (data: SubscriptionResponse) => {
        handlePayment(data);
      },
      onError: (error: unknown) => {
        alert('Failed to create subscription. Please try again.');
      }
    });
  };
  
  // Function to handle copying email to clipboard
  const handleCopyEmail = () => {
    navigator.clipboard.writeText('admin@preimage.ai');
    alert('Email address copied to clipboard!');
  };
  
  // Function to close the contact dialog
  const handleCloseContactDialog = () => {
    setShowContactDialog(false);
  };
  
  const handleDeleteSubscription = () => {
    if (!window.confirm('Are you sure you want to cancel this subscription? This action cannot be undone.')) {
      return;
    }
    const params: DeleteSubscriptionParams = { workspaceSlug: activeWorkspace.slug };
    deleteSubscriptionMutation.mutate(params, {
      onError: (error: unknown) => {
        alert('Failed to cancel subscription. Please try again.');
      }
    });
  };
  
  const handlePauseSubscription = () => {
    if (!isOwner) return;
    if (!window.confirm('Are you sure you want to pause your subscription?')) {
      return;
    }
    const params: PauseSubscriptionParams = { workspaceId: activeWorkspace.id, workspaceSlug: activeWorkspace.slug };
    pauseSubscriptionMutation.mutate(params, {
      onError: (error: unknown) => {
        alert('Failed to pause subscription. Please try again.');
      }
    });
  };
  
  const handleResumeSubscription = () => {
    if (!isOwner) return;
    if (!window.confirm('Are you sure you want to resume your subscription?')) {
      return;
    }
    const params: ResumeSubscriptionParams = { workspaceId: activeWorkspace.id, workspaceSlug: activeWorkspace.slug };
    resumeSubscriptionMutation.mutate(params, {
      onError: (error: unknown) => {
        alert('Failed to resume subscription. Please try again.');
      }
    });
  };

  // Create a skeleton text component for consistent text sections
  const SkeletonText = ({ label, value }: { label: number; value: number }) => (
    <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
      <Box sx={{ color: '#66bb6a', width: `${label}px`, mr: 1 }}>
        <Skeleton variant="rectangular" width="100%" height={22} />
      </Box>
      <Box sx={{ width: `${value}px` }}>
        <Skeleton variant="rectangular" width="100%" height={22} />
      </Box>
    </Box>
  );

  // Create the refresh button component with loading state
  const RefreshButton = () => (
    <Tooltip title="Refresh subscription information">
      <IconButton 
        onClick={handleRefreshSubscription} 
        disabled={isLoadingSubscription}
        sx={{
          color: 'white',
          animation: isLoadingSubscription ? 'spin 1.5s linear infinite' : 'none',
          '@keyframes spin': {
            '0%': {
              transform: 'rotate(0deg)',
            },
            '100%': {
              transform: 'rotate(360deg)',
            },
          },
        }}
      >
        <RefreshIcon />
      </IconButton>
    </Tooltip>
  );

  // Render existing subscription
  if (subscription && subscription !== null && (subscription.hasActiveSubscription || subscription.isPaused)) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1">
            {subscription?.isPaused ? 'Paused Subscription' : 'Active Subscription'}
          </Typography>
          <RefreshButton />
        </Box>
        <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
          {isLoadingSubscription ? (
            <Card sx={{ p: 3, bgcolor: 'transparent', border: '1px solid rgba(255,255,255,0.2)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Skeleton variant="rectangular" height={28} width="50%" />
                <Skeleton variant="rectangular" width={72} height={32} sx={{ borderRadius: 16 }} />
              </Box>
              <Box sx={{ mb: 2 }}>
                <SkeletonText label={70} value={60} />
                <SkeletonText label={120} value={180} />
                <SkeletonText label={110} value={150} />
                <SkeletonText label={70} value={90} />
              </Box>
              
              {/* Buttons */}
              <Stack 
                direction="row" 
                spacing={2} 
                sx={{ mt: 3 }}
              >
                <Skeleton variant="rectangular" height={40} width="48%" sx={{ borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={40} width="48%" sx={{ borderRadius: 1 }} />
              </Stack>
            </Card>
          ) : (
            <Card sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" component="h2">
                  Subscription Details
                </Typography>
                <Chip
                  label={subscription?.isPaused ? 'PAUSED' : subscription?.hasActiveSubscription ? 'ACTIVE' : 'INACTIVE'}
                  color={
                    subscription?.hasActiveSubscription ? 'success' :
                    subscription?.isPaused ? 'warning' :
                    'default'
                  }
                  variant="filled"
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" sx={{ 
                  color: 
                    subscription.hasActiveSubscription ? 'success.main' : 
                    subscription.isPaused ? 'warning.main' : 'error.main', 
                  fontWeight: subscription.isPaused ? 'bold' : 'inherit'
                }}>
                  Status: <strong>{subscription.isPaused ? 'Paused' : subscription.hasActiveSubscription ? 'Active' : 'Inactive'}</strong>
                  {subscription.isPaused && (
                    <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                      Your subscription is currently paused. You can resume it at any time.
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body1">
                  Subscription ID: <strong>{subscription?.subscriptionId}</strong>
                </Typography>
                <Typography variant="body1">
                  Last Updated: <strong>{new Date(subscription?.lastChecked || '').toLocaleString()}</strong>
                </Typography>
                <Typography variant="body1">
                  Source: <strong>{subscription?.source}</strong>
                </Typography>
              </Box>
              <Stack 
                direction="row" 
                spacing={2} 
                sx={{ 
                  mt: 3,
                  '& .MuiButton-root': {
                    flex: 1
                  }
                }}
              >
                {isStaff && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleDeleteSubscription}
                    disabled={deletingSubscription}
                    fullWidth
                  >
                    {deletingSubscription ? 'Canceling...' : 'Cancel'}
                  </Button>
                )}
                {isOwner && subscription?.hasActiveSubscription && !subscription?.isPaused && (
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePauseSubscription}
                    disabled={pausingSubscription || resumingSubscription}
                    fullWidth
                  >
                    {pausingSubscription ? 'Pausing...' : 'Pause'}
                  </Button>
                )}
                {isOwner && subscription?.isPaused && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleResumeSubscription}
                    disabled={pausingSubscription || resumingSubscription}
                    fullWidth
                  >
                    {resumingSubscription ? 'Resuming...' : 'Resume'}
                  </Button>
                )}
              </Stack>
            </Card>
          )}
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">
          Subscription Plans
        </Typography>
        <RefreshButton />
      </Box>
      <Typography variant="body1" paragraph>
        {!isOwner 
          ? 'Only workspace owners can purchase subscriptions.' 
          : 'Choose a subscription plan to get started'}
      </Typography>

      <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
        {isLoadingSubscription ? (
          <Card sx={{ p: 3, bgcolor: 'transparent', border: '1px solid rgba(255,255,255,0.2)' }}>
            <Skeleton variant="rectangular" height={56} sx={{ mb: 3, borderRadius: 1 }} />
            <Box sx={{ mb: 2 }}>
              <SkeletonText label={120} value={160} />
            </Box>
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
          </Card>
        ) : (
          <Card sx={{ p: 3 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="plan-select-label">Select Plan</InputLabel>
              <Select
                labelId="plan-select-label"
                id="plan-select"
                value={selectedPlan}
                label="Select Plan"
                onChange={(e) => setSelectedPlan(e.target.value)}
                disabled={!isOwner}
              >
                {validPlans.map((plan) => (
                  <MenuItem key={plan.name} value={plan.name}>
                    {plan.displayName} - ₹{plan.amount/100}/month
                    {plan.contactOnly && (
                      <Chip 
                        size="small" 
                        label="Contact Us" 
                        color="primary" 
                        sx={{ ml: 1 }}
                      />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleCreateSubscription}
              disabled={loading || !selectedPlan || !isOwner}
            >
              {loading ? 'Processing...' : 'Subscribe Now'}
            </Button>
          </Card>
        )}
      </Box>
      
      {/* Enterprise Plan Contact Dialog */}
      {showContactDialog && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={handleCloseContactDialog}
        >
          <Card 
            sx={{ 
              p: 4, 
              maxWidth: 500, 
              width: '90%',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
              Enterprise Plan Inquiry
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              For Enterprise plan inquiries, please contact our team directly at:
            </Typography>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                backgroundColor: 'rgba(0,0,0,0.05)',
                p: 2,
                borderRadius: 1,
                mb: 3
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                admin@preimage.ai
              </Typography>
              <Button 
                variant="outlined" 
                size="small"
                onClick={handleCopyEmail}
              >
                Copy
              </Button>
            </Box>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              Our team will get back to you with custom pricing and features tailored to your organization's needs.
            </Typography>
            <Button 
              variant="contained" 
              fullWidth
              onClick={handleCloseContactDialog}
            >
              Close
            </Button>
          </Card>
        </Box>
      )}
    </Container>
  );
};

export default ExperimentalPayments;
